import { IonButton } from '@ionic/react'
import { useForm, Controller } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'

import { StyledError } from '../../../components/ui/form/errors'
import { TextInput, NumericInput } from '../../../components/ui/form/input'
import { SchedulePicker } from '../../../components/ui/form/schedule-picker'
import { updateLoyaltyBonusRuleInputs } from '../../../loyalty/loyalty.schema'
import { useUpdateLoyaltyBonusRule } from '../../../loyalty/mutations'

import type {
  LoyaltyBonusRule,
  UpdateLoyaltyBonusRuleInputs,
} from '../../../loyalty/loyalty.schema'
import type { BonusAvailability } from './types'

type Props = {
  availability: BonusAvailability
  bonus: LoyaltyBonusRule
  onSubmit: () => void
}

function EditBonusForm({ availability, bonus, onSubmit }: Props) {
  const updateBonusRule = useUpdateLoyaltyBonusRule()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<UpdateLoyaltyBonusRuleInputs>({
    defaultValues: {
      ...bonus,
      endDate: bonus.endDate,
      startDate: bonus.startDate,
    },
    resolver: zodResolver(updateLoyaltyBonusRuleInputs),
  })

  function handleCreateBonusRule(data: LoyaltyBonusRule) {
    updateBonusRule.mutate(data, {
      onSuccess: onSubmit,
    })
  }

  return (
    <form className="space-y-2" onSubmit={handleSubmit(handleCreateBonusRule)}>
      {bonus.text ? (
        <div className="flex items-end gap-x-2">
          <div className="flex-grow">
            <TextInput control={control} name="text" color="light" />
            <ErrorMessage as={StyledError} errors={errors} name="text" />
          </div>
          <span className="text-sm">(enter custom reward)</span>
        </div>
      ) : bonus.value ? (
        <div className="flex items-center gap-x-2">
          <span className="text-lg">$</span>
          <div>
            <NumericInput control={control} name="value" color="light" />
            <ErrorMessage as={StyledError} errors={errors} name="value" />
          </div>
          <span className="text-lg">in rewards</span>
        </div>
      ) : null}

      <div className="py-3">
        <p className="text-lg">Bonus Availability:</p>

        {availability === 'expiration' ? (
          <>
            <div className="flex items-center gap-x-2">
              <span className="text-lg">Expires in</span>
              <NumericInput
                control={control}
                name="expirationPointsDays"
                color="light"
              />
              <span className="text-lg">days</span>
            </div>
            <ErrorMessage
              as={StyledError}
              errors={errors}
              name="expirationPointsDays"
            />
          </>
        ) : availability === 'range' ? (
          <>
            <div className="flex flex-col items-start gap-y-2">
              <div className="flex items-center gap-x-1.5">
                <span>Start Date:</span>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SchedulePicker
                      color="primary"
                      label="Choose a Date"
                      value={value}
                      onChange={(e) => onChange(e.detail.value)}
                      onReset={() => {
                        onChange(null)
                      }}
                    />
                  )}
                />
              </div>
              <div className="flex items-center gap-x-1.5">
                <span>End Date:</span>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SchedulePicker
                      color="primary"
                      label="Choose a Date"
                      value={value}
                      onChange={(e) => onChange(e.detail.value)}
                      onReset={() => {
                        onChange(null)
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <ErrorMessage as={StyledError} errors={errors} name="startDate" />
            <ErrorMessage as={StyledError} errors={errors} name="endDate" />
          </>
        ) : null}
      </div>
      <IonButton
        color="yellow"
        size="small"
        type="submit"
        disabled={isSubmitting}
      >
        Save
      </IonButton>
    </form>
  )
}

export { EditBonusForm }
